import { ReactNode } from 'react';

import { NumberStepper } from 'pages/custom_tests/components/custom_test_wizard/components/number_stepper/NumberStepper';
import { useCustomTestWizard } from 'pages/custom_tests/components/custom_test_wizard/context';
import { EWizardMode } from 'pages/custom_tests/components/custom_test_wizard/machine';
import { FormattedMessage, useIntl } from 'react-intl';
import { Col, Form, Row } from 'react-bootstrap';

export const TestSettingsStep = () => {

    const { minScore, minutes, modeId, orderMode, randomly, selectedQuestions,
        setProviderState, useRandomly } = useCustomTestWizard();
    const { formatMessage } = useIntl();

    const enableSubset = () => setProviderState({
        randomly: randomly > 0 ? randomly : (selectedQuestions || 1),
        useRandomly: true
    });

    const renderRadioCard = (params: { active: boolean; children?: ReactNode; description: string; onSelect: () => void; title: string }) => (
        <div
            className={`border rounded p-3 ${params.active ? 'border-primary bg-primary bg-opacity-10' : ''}`}
            onClick={params.onSelect}
            role={'button'}
        >
            <div className={'d-flex align-items-start gap-2'}>
                <input
                    checked={params.active}
                    className={'form-check-input mt-1 flex-shrink-0'}
                    onChange={params.onSelect}
                    type={'radio'}
                />
                <div className={'flex-fill'}>
                    <div className={'fw-semibold'}>
                        {params.title}
                    </div>
                    <div className={'small text-muted'}>
                        {params.description}
                    </div>
                    {params.children}
                </div>
            </div>
        </div>
    );

    return (
        <div className={'d-flex flex-column gap-4 py-2'}>

            <Row className={'g-4'}>
                <Col md={4} sm={12}>
                    <h6 className={'fw-bold text-primary'}>
                        <FormattedMessage defaultMessage="Scoring" id="scoring.c27bbc1e" />
                    </h6>
                    <Form.Label className={'small fw-semibold'}>
                        <FormattedMessage defaultMessage="Set passing score" id="set_passing_score.392ac8cf" />
                    </Form.Label>
                    <div>
                        <NumberStepper
                            ariaLabel={formatMessage({ id: 'set_passing_score.392ac8cf', defaultMessage: 'Set passing score' })}
                            max={100}
                            min={1}
                            onChange={value => setProviderState({ minScore: value, minScoreTouched: true })}
                            suffix={'%'}
                            value={minScore}
                        />
                    </div>
                </Col>

                <Col md={4} sm={12}>
                    <h6 className={'fw-bold text-primary'}>
                        <FormattedMessage defaultMessage="Time Limit" id="time_limit.50b63b24" />
                    </h6>
                    <Form.Label className={'small fw-semibold'}>
                        <FormattedMessage defaultMessage="Set limit" id="set_limit.a9744a7c" />
                    </Form.Label>
                    <div>
                        <NumberStepper
                            ariaLabel={formatMessage({ id: 'set_limit.a9744a7c', defaultMessage: 'Set limit' })}
                            max={600}
                            min={1}
                            onChange={value => setProviderState({ minutes: value, minutesTouched: true })}
                            suffix={formatMessage({ id: 'min.834408a4', defaultMessage: 'min' })}
                            value={minutes}
                        />
                    </div>
                </Col>

                <Col md={4} sm={12}>
                    <h6 className={'fw-bold text-primary'}>
                        <FormattedMessage defaultMessage="Testing Mode" id="testing_mode.3727f585" />
                    </h6>
                    <Form.Label className={'small fw-semibold'}>
                        <FormattedMessage defaultMessage="Select Mode" id="select_mode.cb21a910" />
                    </Form.Label>
                    <Form.Select onChange={e => setProviderState({ modeId: parseInt(e.target.value, 10) })} value={modeId}>
                        {/* <option> only renders string children; a React element shows "[object Object]" */}
                        <option disabled value={0}>
                            {formatMessage({ id: 'select.ea1021d6', defaultMessage: 'Select...' })}
                        </option>
                        <option value={EWizardMode.TESTING}>
                            {formatMessage({ id: 'testing.a439953c', defaultMessage: 'Testing' })}
                        </option>
                        <option value={EWizardMode.TRAINING}>
                            {formatMessage({ id: 'training.1fce7301', defaultMessage: 'Training' })}
                        </option>
                        <option value={EWizardMode.BOTH}>
                            {formatMessage({ id: 'both.8074bfd4', defaultMessage: 'Both' })}
                        </option>
                    </Form.Select>
                </Col>
            </Row>

            <hr className={'my-0'} />

            <div>
                <h6 className={'fw-bold text-primary mb-3'}>
                    <FormattedMessage defaultMessage="Question Settings" id="question_settings.45a4dc95" />
                </h6>

                <Row className={'g-4'}>
                    <Col md={6} sm={12}>
                        <Form.Label className={'small fw-semibold'}>
                            <FormattedMessage defaultMessage="Set delivery mode" id="set_delivery_mode.e02207c7" />
                        </Form.Label>
                        <div className={'d-flex flex-column gap-2'}>
                            {renderRadioCard({
                                active: !useRandomly,
                                description: formatMessage({ id: 'all_students_complete_the_same_set_of_qu.c55f393c', defaultMessage: 'All students complete the same set of questions, ensuring a consistent testing experience' }),
                                onSelect: () => setProviderState({ useRandomly: false }),
                                title: formatMessage({ id: 'deliver_all_selected_questions.e17d720f', defaultMessage: 'Deliver all selected questions' })
                            })}
                            {renderRadioCard({
                                active: useRandomly,
                                children: useRandomly && (
                                    <div className={'mt-2 d-flex flex-column gap-1'} onClick={e => e.stopPropagation()}>
                                        <NumberStepper
                                            ariaLabel={formatMessage({ id: 'number_of_questions.e9b54307', defaultMessage: 'Number of questions' })}
                                            max={selectedQuestions > 0 ? selectedQuestions : undefined}
                                            min={1}
                                            onChange={value => setProviderState({ randomly: value })}
                                            suffix={formatMessage({ id: 'questions.04b685ee', defaultMessage: 'questions' })}
                                            value={randomly}
                                        />
                                        <span className={'small text-muted'}>
                                            <FormattedMessage defaultMessage="Drawn at random from {count} question bank" id="drawn_at_random_from_count_question_bank.8779faaf" values={{ count: selectedQuestions }} />
                                        </span>
                                    </div>
                                ),
                                description: formatMessage({ id: 'each_student_gets_a_unique_random_set_of.64acf08c', defaultMessage: 'Each student gets a unique random set of questions, helping reduce exposure across sittings' }),
                                onSelect: enableSubset,
                                title: formatMessage({ id: 'deliver_a_random_subset.4a78af6b', defaultMessage: 'Deliver a random subset' })
                            })}
                        </div>
                    </Col>

                    <Col md={6} sm={12}>
                        <Form.Label className={'small fw-semibold'}>
                            <FormattedMessage defaultMessage="Set order mode" id="set_order_mode.e9687b5b" />
                        </Form.Label>
                        <div className={'d-flex flex-column gap-2'}>
                            {renderRadioCard({
                                active: orderMode === 'randomized',
                                description: formatMessage({ id: 'questions_are_shown_in_a_different_order.bdec304f', defaultMessage: 'Questions are shown in a different order for each student, helping reduce pattern recognition and support readiness checks' }),
                                onSelect: () => setProviderState({ orderMode: 'randomized' }),
                                title: formatMessage({ id: 'fully_randomized.3abbb070', defaultMessage: 'Fully randomized' })
                            })}
                            {renderRadioCard({
                                active: orderMode === 'grouped',
                                description: formatMessage({ id: 'within_each_objective_questions_are_stil.854cec4a', defaultMessage: 'Within each objective, questions are still presented in a randomized order, helping support targeted remediation while keeping reviews focused and varied.' }),
                                onSelect: () => setProviderState({ orderMode: 'grouped' }),
                                title: formatMessage({ id: 'grouped_by_objective.1ea8241f', defaultMessage: 'Grouped by objective' })
                            })}
                        </div>
                    </Col>
                </Row>
            </div>

        </div>
    );

};
